import axios, { AxiosResponse } from "axios";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

import type { AuthRole } from "../../types/auth";
import { UserTokenPayload } from "./authService.types";
import { logger } from "utils/logger";
import { API_BASE_URL } from "../apiBaseUrl";

const authCookieOptions = {
  sameSite: "Lax" as const,
  secure: window.location.protocol === "https:",
};

export const registerUser = async (
  formData: Record<string, string>,
  confirmPassword: string,
  role: AuthRole = "teacher",
  linkToken?: string,
): Promise<AxiosResponse<any, any>> => {
  if (role === "student") {
    const registrationData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      password: formData.password,
      confirm_password: confirmPassword,
      verification_code: formData.verification_code,
      ...(linkToken ? { link_token: linkToken } : {}),
    };
    const endpoint = linkToken
      ? `${API_BASE_URL}/student-auth/register-from-telegram/`
      : `${API_BASE_URL}/student-auth/register/`;
    return axios.post(endpoint, registrationData);
  }

  const registrationData = {
    email: formData.email,
    password: formData.password,
    confirm_password: confirmPassword,
    first_name: formData.firstName,
    last_name: formData.lastName,
    organization_name: formData.organization_name,
    verification_code: formData.verification_code,
  };
  return axios.post(`${API_BASE_URL}/users/`, registrationData);
};

export const sendPasswordResetCode = async (
  email: string,
  type: string = "reset",
  role: AuthRole = "teacher",
): Promise<void> => {
  await axios.post(
    `${API_BASE_URL}/auth/send-reset-code/`,
    { email, type, role },
    { withCredentials: true },
  );
};

export const verifyPasswordResetCode = async (
  resetCode: string,
  email: string,
  role: AuthRole = "teacher",
): Promise<void> => {
  await axios.post(`${API_BASE_URL}/auth/verify-reset-code/`, {
    resetCode,
    email,
    role,
    type: "reset",
  });
};

export const resetPassword = async (data: {
  email: string;
  password: string;
  confirmPassword: string;
  role?: AuthRole;
}): Promise<void> => {
  await axios.post(`${API_BASE_URL}/auth/reset-password/`, data);
};

export const sendVerificationCode = async (
  email: string,
  type: string,
  role: AuthRole = "teacher",
  password?: string,
  confirmPassword?: string,
): Promise<void> => {
  await axios.post(`${API_BASE_URL}/auth/send-verification-code/`, {
    email,
    type,
    role,
    password,
    confirm_password: confirmPassword,
  });
};

export const getAuthTokens = async (
  email: string,
  password: string,
  role: AuthRole = "teacher",
) => {
  const endpoint = role === "student" ? "/student-auth/token/" : "/auth/token/";
  const response = await axios.post(`${API_BASE_URL}${endpoint}`, { email, password });
  const { access, refresh, first_name, last_name } = response.data;

  Cookies.set("access_token", access, authCookieOptions);
  Cookies.set("refresh_token", refresh, authCookieOptions);
  Cookies.set("auth_role", role, authCookieOptions);
  Cookies.set("email", email, authCookieOptions);
  Cookies.set("first_name", first_name || "", authCookieOptions);
  Cookies.set("last_name", last_name || "", authCookieOptions);

  if (role === "student") {
    const studentId = String(response.data.student_id);
    Cookies.set("student_id", studentId, authCookieOptions);
    Cookies.remove("user_id");
    return { access, refresh, student_id: studentId, role };
  }

  const decodedToken = jwtDecode<UserTokenPayload>(access);
  const userId = String(decodedToken.user_id);
  Cookies.set("user_id", userId, authCookieOptions);
  Cookies.remove("student_id");
  return { access, refresh, user_id: userId, role };
};

export const createReferral = async (userId: string, referralToken: string): Promise<void> => {
  if (!referralToken) return;
  try {
    await axios.post(`${API_BASE_URL}/auth/create-referral/`, {
      user_id: userId,
      referral_token: referralToken,
    });
  } catch (error) {
    logger.error("Ошибка при создании реферального кода:", error);
    throw error;
  }
};